import { Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PlaceholderNote } from "../components/ComingSoon";
import { LiveForm, Field, TextInput, TextArea } from "../components/form";
import { CrisisActions } from "../components/CrisisActions";
import { CONTACT } from "../content/site";

export default function Contact() {
  return (
    <>
      <PageHero eyebrow="Contact" title="Contact TJ's Light" testId="contact-hero">
        <p>
          Questions about the foundation, Walking for Hope, volunteering, or partnering?
          We'd love to hear from you.
        </p>
      </PageHero>

      <section aria-labelledby="contact-details-heading" className="px-4 pb-20 sm:px-8 lg:px-16">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 id="contact-details-heading" className="font-serif text-2xl font-normal text-ink sm:text-3xl">
                Ways to reach us
              </h2>
              <div className="mt-6 space-y-4">
                {CONTACT.email ? (
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="flex min-h-[52px] items-center gap-3 rounded-xl border border-sage/15 bg-white px-5 py-4 font-semibold text-sage transition-colors hover:bg-sage-soft"
                    data-testid="contact-email-link"
                  >
                    <Mail className="h-5 w-5" aria-hidden="true" /> {CONTACT.email}
                  </a>
                ) : (
                  <PlaceholderNote testId="contact-email-placeholder">
                    <strong className="font-semibold text-ink">Email address — coming soon.</strong>{" "}
                    The foundation's email address will be published here once it is set up.
                  </PlaceholderNote>
                )}
                {CONTACT.socials.length === 0 && (
                  <PlaceholderNote testId="contact-social-placeholder">
                    <strong className="font-semibold text-ink">Social media — coming soon.</strong>{" "}
                    Links to the foundation's official social accounts will appear here
                    once they are confirmed.
                  </PlaceholderNote>
                )}
                {CONTACT.socials.length > 0 && (
                  <ul className="space-y-2" data-testid="contact-social-list">
                    {CONTACT.socials.map((s) => (
                      <li key={s.name}>
                        <a
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-h-[48px] items-center rounded-xl border border-sage/15 bg-white px-5 py-3 font-semibold text-sage transition-colors hover:bg-sage-soft"
                        >
                          {s.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {CONTACT.note && (
                <p className="mt-6 text-sm leading-relaxed text-body" data-testid="contact-note">{CONTACT.note}</p>
              )}
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                We read every message with care. A reply-time commitment will be added
                here once the foundation confirms one.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="rounded-2xl border border-sage/10 bg-white p-6 sm:p-8">
                <h2 className="font-serif text-2xl font-normal text-ink">Send a message</h2>
                <div
                  className="mt-4 rounded-xl border-l-4 border-gold bg-gold-soft/50 p-4 text-sm leading-relaxed text-body"
                  role="note"
                  data-testid="contact-crisis-note"
                >
                  This form is not monitored as a crisis service. If you need immediate
                  support, call or text 988. If there is immediate danger, call 911.
                </div>
                <div className="mt-4">
                  <CrisisActions />
                </div>
                <div className="mt-8">
                  {CONTACT.formEnabled !== false ? (
                    <LiveForm
                      type="contact"
                      testId="contact-form"
                      submitLabel="Send message"
                      successMessage="Thank you — your message has been received."
                    >
                      <div className="space-y-6">
                        <Field label="Name" id="ct-name" required>
                          <TextInput id="ct-name" required placeholder="Your full name" testId="ct-name-input" />
                        </Field>
                        <Field label="Email" id="ct-email" required>
                          <TextInput id="ct-email" type="email" required placeholder="you@example.org" testId="ct-email-input" />
                        </Field>
                        <Field label="Topic" id="ct-topic">
                          <TextInput id="ct-topic" placeholder="e.g. Walking for Hope, volunteering" testId="ct-topic-input" />
                        </Field>
                        <Field label="Message" id="ct-message" required>
                          <TextArea id="ct-message" rows={5} placeholder="How can we help?" testId="ct-message-textarea" />
                        </Field>
                      </div>
                    </LiveForm>
                  ) : (
                    <PlaceholderNote testId="contact-form-closed-note">
                      <strong className="font-semibold text-ink">The contact form is paused.</strong>{" "}
                      Please use the email address on this page once it is published.
                    </PlaceholderNote>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
